import { createSlice } from "@reduxjs/toolkit";
import { hideLoading, showLoading } from "react-redux-loading-bar";

import {
  deleteCpmsConnection,
  getCpmsConnections,
  createCpmsConnection,
  revokeCpmsConnection,
  testCpmsConnection,
  recoverCpmsObservationFeed,
} from "./cpmsConnectionsAPI";

export const cpmsErrorMessage = (error) =>
  error.response?.data?.detail ??
  error.response?.data?.message ??
  error.response?.data?.error ??
  error.message ??
  "Request failed";

const initialState = {
  connections: [],
  status: "idle",
  error: "",
  successMessage: "",
  createModalOpen: false,
  revokeConnection: null,
  deleteConnection: null,
  pendingAction: "",
  recoverFeed: null,
};

export const cpmsConnectionsSlice = createSlice({
  name: "cpmsConnections",
  initialState,
  reducers: {
    setConnections: (state, action) => {
      state.connections = action.payload;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setCreateModalOpen: (state, action) => {
      state.createModalOpen = action.payload;
    },
    setRevokeConnection: (state, action) => {
      state.revokeConnection = action.payload;
    },
    setDeleteConnection: (state, action) => {
      state.deleteConnection = action.payload;
    },
    setPendingAction: (state, action) => {
      state.pendingAction = action.payload;
    },
    setRecoverFeed: (state, action) => {
      state.recoverFeed = action.payload;
    },
    replaceConnection: (state, action) => {
      const index = state.connections.findIndex(({ id }) => id === action.payload.id);
      if (index >= 0) state.connections[index] = action.payload;
    },
    removeConnection: (state, action) => {
      state.connections = state.connections.filter(({ id }) => id !== action.payload);
    },
  },
});

export const {
  setConnections,
  setStatus,
  setError,
  setSuccessMessage,
  setCreateModalOpen,
  setRevokeConnection,
  setDeleteConnection,
  setPendingAction,
  setRecoverFeed,
  replaceConnection,
  removeConnection,
} = cpmsConnectionsSlice.actions;

export const selectCpmsConnections = (state) => state.cpmsConnections;

export const handleGetCpmsConnections = () => async (dispatch) => {
  dispatch(setStatus("loading"));
  dispatch(setError(""));
  try {
    const response = await getCpmsConnections();
    dispatch(setConnections(response.data));
    dispatch(setStatus("succeeded"));
  } catch (error) {
    dispatch(setError(cpmsErrorMessage(error)));
    dispatch(setStatus("failed"));
  }
};

export const handleCreateCpmsConnection = (connection) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setPendingAction("create"));
  dispatch(setError(""));
  dispatch(setSuccessMessage(""));
  try {
    const response = await createCpmsConnection(connection);
    dispatch(setCreateModalOpen(false));
    await dispatch(handleGetCpmsConnections());
    if (response.data.status === "FAILED") {
      dispatch(setError(
        `${response.data.name} was created, but validation failed${response.data.lastValidationError ? `: ${response.data.lastValidationError}` : "."} Fix the CPMS configuration, then use Test.`
      ));
    } else {
      dispatch(setSuccessMessage(`Created ${response.data.name}.`));
    }
    return true;
  } catch (error) {
    dispatch(setError(cpmsErrorMessage(error)));
    return false;
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

const lifecycleAction = (action, success) => (id) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setPendingAction(`${action.name}:${id}`));
  dispatch(setError(""));
  dispatch(setSuccessMessage(""));
  try {
    const response = await action(id);
    dispatch(replaceConnection(response.data));
    if (response.data.status === "FAILED") {
      dispatch(setError(
        `Connection test failed${response.data.lastValidationError ? `: ${response.data.lastValidationError}` : "."}`
      ));
    } else {
      dispatch(setSuccessMessage(success));
    }
  } catch (error) {
    dispatch(setError(cpmsErrorMessage(error)));
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

export const handleTestCpmsConnection = lifecycleAction(
  testCpmsConnection,
  "Connection test completed."
);

export const handleRevokeCpmsConnection = (id) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setPendingAction(`revoke:${id}`));
  dispatch(setError(""));
  dispatch(setSuccessMessage(""));
  try {
    const response = await revokeCpmsConnection(id);
    dispatch(replaceConnection(response.data));
    dispatch(setRevokeConnection(null));
    dispatch(setSuccessMessage("Connection disabled."));
  } catch (error) {
    dispatch(setError(cpmsErrorMessage(error)));
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

export const handleDeleteCpmsConnection = (id) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setPendingAction(`delete:${id}`));
  dispatch(setError(""));
  dispatch(setSuccessMessage(""));
  try {
    await deleteCpmsConnection(id);
    dispatch(removeConnection(id));
    dispatch(setDeleteConnection(null));
    dispatch(setSuccessMessage("Connection deleted."));
  } catch (error) {
    dispatch(
      setError(
        error.response?.status === 409
          ? "This connection is used by a season pass plan. Change or deactivate that plan before deleting the connection."
          : cpmsErrorMessage(error)
      )
    );
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

export const handleRecoverCpmsObservationFeed = (target) => async (dispatch) => {
  const { connectionId, siteId, feed } = target;
  dispatch(showLoading());
  dispatch(setPendingAction(`recover:${connectionId}:${siteId}:${feed}`));
  dispatch(setError(""));
  dispatch(setSuccessMessage(""));
  try {
    await recoverCpmsObservationFeed(connectionId, siteId, feed);
    dispatch(setRecoverFeed(null));
    await dispatch(handleGetCpmsConnections());
    dispatch(setSuccessMessage(`${feed} observation recovery started for site ${siteId}.`));
  } catch (error) {
    dispatch(setError(cpmsErrorMessage(error)));
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

export default cpmsConnectionsSlice.reducer;
